import { EmptyState, Space, WithOverlayLoader } from '@procurify/ui'

import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { ResetPagination } from '@webapp/core/components/ResetPagination'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { AuditLogListStyled } from './styles/AuditLogListStyled'
import { useAuditLogListPresenter } from './useAuditLogListPresenter'
import {
  AuditLogRecord,
  AuditLogRecordLoader,
  type IAuditLogRecordProps,
} from '../../views/AuditLogRecord'

export interface IAuditLogListPresenterProps {
  contentTypeId: number
  /** Omitted → module-wide log for the content type (BHV-030) */
  objectId?: string
  showPageLimit?: boolean
  /** Names the list of entries; defaults to "Audit log" */
  ['aria-label']?: string
  /**
   * Level of each entry title, one below the heading the host puts over the
   * log. Defaults to `h5`, under an h4 section heading; a `ModalDialog` host,
   * whose title is h3, passes `h4`.
   */
  headingLevel?: IAuditLogRecordProps['headingLevel']
}

const AuditLogList = ({
  contentTypeId,
  objectId,
  showPageLimit = true,
  'aria-label': ariaLabel,
  headingLevel,
}: IAuditLogListPresenterProps) => {
  const intl = useProcurifyIntl()

  const {
    data: { auditLog },
    state: {
      showLoader,
      showPageChangeLoader,
      showAuditLog,
      showLoadError,
      showPager,
    },
  } = useAuditLogListPresenter({
    contentTypeId,
    objectId,
  })

  return (
    <div>
      {showLoader && (
        <AuditLogListStyled>
          <AuditLogRecordLoader headingLevel={headingLevel} />
          <AuditLogRecordLoader headingLevel={headingLevel} />
          <AuditLogRecordLoader headingLevel={headingLevel} />
        </AuditLogListStyled>
      )}

      {!showLoader && (
        <PaginationControls id='audit-log'>
          <Space size='large'>
            <WithOverlayLoader
              visible={showPageChangeLoader}
              text={intl.formatMessage(messages.loadingText)}
            >
              {showAuditLog ? (
                <AuditLogListStyled
                  aria-label={
                    ariaLabel ?? intl.formatMessage(messages.listAriaLabel)
                  }
                >
                  {auditLog.map((auditLogRecord, index) => (
                    <AuditLogRecord
                      key={`audit-log-${index}`}
                      status={auditLogRecord.status}
                      title={auditLogRecord.title}
                      userName={auditLogRecord.changedBy.fullName}
                      changedOn={auditLogRecord.changedOn}
                      changedFields={auditLogRecord.changedFields}
                      delegateeUserName={auditLogRecord.delegatee?.fullName}
                      headingLevel={headingLevel}
                    />
                  ))}
                </AuditLogListStyled>
              ) : (
                <EmptyState
                  context='history'
                  header={
                    showLoadError
                      ? intl.formatMessage(messages.errorStateText)
                      : undefined
                  }
                />
              )}
            </WithOverlayLoader>
            {showPager && (
              <Space direction='horizontal' justify='space-between'>
                {showPageLimit && <PaginationControls.PageLimit />}
                <PaginationControls.Page />
              </Space>
            )}
          </Space>
        </PaginationControls>
      )}
    </div>
  )
}

// 'audit-log' is one app-wide pagination slot shared by every audit log, so
// each log resets it before its first fetch and opens on page 1.
export const AuditLogListPresenter = (props: IAuditLogListPresenterProps) => (
  <ResetPagination
    key={`${props.contentTypeId}:${props.objectId ?? ''}`}
    id='audit-log'
  >
    <AuditLogList {...props} />
  </ResetPagination>
)
